/**
 * Bubbles from the holes (spec 02 §8 Spawner, §7.7), ported from rebb64 D_0BED - misnamed
 * `enemy_spawn_handler`: it spawns bubbles, not enemies - and the letter branch it self-modifies into
 * (L_0CA8). Only on levels with the EXTEND flag (D_3A6C turns the JSR at $0A38 into a BIT otherwise).
 */

import { windAt } from '../playfield';
import { rngOf } from '../random';
import { FIRE, FULL, LETTER, LIGHTNING, WATER, isSpecialCode } from './codes';
import { addBubble, freeSlot, mapSolid } from './slots';
import { systemOf } from './system';
import {
  HOLE_BUBBLE_LIFE, HOLE_COLS, HOLE_ROW_BOTTOM, HOLE_ROW_TOP, HOLE_TEST_COLS, MAX_SPECIALS, SPAWN_PERIOD,
  SPAWN_SKIP,
} from './tuning';
import type { BubbleContent, World } from '../types';

/**
 * One pass: every 23rd attempts (the timer counts $16..0), while a monster lives ($4A); 40/256 of
 * attempts are skipped; a random hole of four is used only if its map cell is open and its wind
 * blows in (top: down; bottom: up); none while 10 specials float, or with no slot free.
 */
export function spawnFromHoles(world: World): void {
  const sys = systemOf(world);
  if (!sys.spawnOn) return;
  if (sys.spawnTimer !== 0) {
    sys.spawnTimer--;
    return;
  }
  sys.spawnTimer = SPAWN_PERIOD;
  if (!world.monsters.some((m) => m.state !== 'dead')) return;
  const rng = rngOf(world);
  if (rng.int(256) < SPAWN_SKIP) return;
  const hole = rng.int(256) & 3;
  const top = hole < 2;
  const right = (hole & 1) === 1;
  const testCol = right ? HOLE_TEST_COLS[1] : HOLE_TEST_COLS[0];
  const testRow = top ? 0 : 24;
  if (mapSolid(world, testCol, testRow)) return;
  if (windAt(world.playfield, testCol, testRow) !== (top ? 'down' : 'up')) return;
  if (world.bubbles.filter((b) => isSpecialCode(b.motion.code)).length >= MAX_SPECIALS) return;
  const slot = freeSlot(world);
  if (slot < 0) return;
  const col = right ? HOLE_COLS[1] : HOLE_COLS[0];
  const row = top ? HOLE_ROW_TOP : HOLE_ROW_BOTTOM;
  const roll = rng.int(256);
  const kind = sys.letters > 0 ? letterCode(world) : typeCode(roll & sys.spawnMask);
  addBubble(world, slot, {
    x: col * 8 + 0x14, y: row * 8 + 0x15, owner: -1, content: contentOf(kind),
    // Letters keep the slot's old timer on the C64 (L_0CA8 skips $0CC7); they never time out anyway.
    motion: { code: kind, row, subY: 0, steps: 0, timer: kind >= LETTER ? 0 : HOLE_BUBBLE_LIFE },
  });
}

/** D_0CC1: rand & $0CC2 (water 1, fire 2, lightning 4) - the lowest bit set picks the special. */
function typeCode(bits: number): number {
  if (bits & 1) return WATER;
  if (bits & 2) return FIRE;
  if (bits & 4) return LIGHTNING;
  return FULL;
}

/**
 * L_0CA8: letter = (rand + the last letter) & 7, folded to 0..4 by & 3 above 4 - so E 1/8, X 2/8,
 * T 2/8, N 2/8, D 1/8 - one fewer to come.
 */
function letterCode(world: World): number {
  const sys = systemOf(world);
  sys.letters--;
  let n = (rngOf(world).int(256) + sys.letterSeed) & 7;
  if (n >= 5) n &= 3;
  sys.letterSeed = n;
  return LETTER + 2 * n;
}

function contentOf(code: number): BubbleContent {
  if (code === WATER) return { kind: 'water' };
  if (code === FIRE) return { kind: 'fire' };
  if (code === LIGHTNING) return { kind: 'lightning' };
  if (code >= LETTER) return { kind: 'letter', letter: (code - LETTER) >> 1 };
  return { kind: 'empty' };
}
